import React, { useMemo } from "react";
import Image from "next/image";
import { SavedOption } from "v3/components/checkout/SavedOption";
import { AllowedPaymentMethod, CardType } from "v3/services/graphql/generated";
import { getCloudflareLoader } from "@pepdirect/helpers/cloudflareImages";
import { getCreditCardImg, isApplePaySupported } from "v3/helpers/payment";
import { paypalPaymentTypeImg, paymentImgPaths } from "v3/constants/payment";
import { endpoints } from "config";
import {
  StyledCreditCardOption,
  StyledCreditCardsContainer,
  StyledPaymentTypeSelector,
  StyledPaymentTypeTitle,
  StyledPaypalImg,
} from "./index.styled";

interface PaymentTypeSelectorProps {
  allowedPaymentMethods: AllowedPaymentMethod[];
  selectedPaymentType: string | null;
  setSelectedPaymentType: (paymentMethod: AllowedPaymentMethod) => void;
  validCreditCardTypes?: CardType[];
}

export const PaymentTypeSelector = ({
  allowedPaymentMethods,
  selectedPaymentType,
  setSelectedPaymentType,
  validCreditCardTypes,
}: PaymentTypeSelectorProps) => {
  const isApplePayAllowed = useMemo(
    () =>
      allowedPaymentMethods.includes(AllowedPaymentMethod.ApplePay) &&
      isApplePaySupported(),
    [allowedPaymentMethods]
  );

  const handleClick = (paymentMethod: AllowedPaymentMethod) => () =>
    setSelectedPaymentType(paymentMethod);

  return (
    <StyledPaymentTypeSelector>
      {allowedPaymentMethods.includes(AllowedPaymentMethod.CreditCard) &&
        !!validCreditCardTypes?.length && (
          <SavedOption
            checked={selectedPaymentType === AllowedPaymentMethod.CreditCard}
            handleClick={handleClick(AllowedPaymentMethod.CreditCard)}
          >
            <StyledCreditCardOption>
              <StyledPaymentTypeTitle>Credit Card</StyledPaymentTypeTitle>
              <StyledCreditCardsContainer>
                {validCreditCardTypes.map(({ name }) => (
                  <Image
                    alt={name}
                    height="24"
                    key={name}
                    src={getCreditCardImg(name)}
                    loader={getCloudflareLoader(endpoints.cdn)}
                    width="35"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                    }}
                  />
                ))}
              </StyledCreditCardsContainer>
            </StyledCreditCardOption>
          </SavedOption>
        )}
      {allowedPaymentMethods.includes(AllowedPaymentMethod.Paypal) && (
        <SavedOption
          checked={selectedPaymentType === AllowedPaymentMethod.Paypal}
          handleClick={handleClick(AllowedPaymentMethod.Paypal)}
        >
          <StyledPaypalImg
            src={paypalPaymentTypeImg}
            height="24"
            width="88"
            alt="paypal"
          />
        </SavedOption>
      )}
      {isApplePayAllowed && (
        <SavedOption
          checked={selectedPaymentType === AllowedPaymentMethod.ApplePay}
          handleClick={handleClick(AllowedPaymentMethod.ApplePay)}
        >
          <StyledCreditCardOption>
            <StyledPaypalImg
              src={paymentImgPaths.applePay}
              height="24"
              alt="apple-pay"
            />
          </StyledCreditCardOption>
        </SavedOption>
      )}
    </StyledPaymentTypeSelector>
  );
};
